import type { AttendanceReportRow } from '../types/attendanceReport';

/**
 * Attendance day status calculated by the backend (GET attendance APIs return
 * it as `dayStatus`; report rows carry it as dayStatus* columns). Present /
 * Half-Day / Absent comes from the employee's Expected Work Hours, Expected
 * Half-Day Work Hours, approved leave and late-arrival policy on the server —
 * screens only display it.
 */
export type AttendanceDayStatusTone = AttendanceReportRow['statusTone'];

export type AttendanceDayStatus = {
  code: string;
  label: string;
  tone: AttendanceDayStatusTone;
  notes: string[];
  isFinal: boolean;
  workingDay: boolean;
  presentDays: number;
  absentDays: number;
  leaveDays: number;
  workedMinutes: number | null;
  expectedWorkMinutes: number | null;
  halfDayWorkMinutes: number | null;
};

const TONES: readonly AttendanceDayStatusTone[] = [
  'present',
  'late',
  'wfh',
  'leave',
  'absent',
  'shortHours',
  'neutral',
];

function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function numberOr(value: unknown, fallback: number): number {
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) && value !== null && value !== '' ? number : fallback;
}

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function tone(value: unknown): AttendanceDayStatusTone {
  return TONES.includes(value as AttendanceDayStatusTone)
    ? (value as AttendanceDayStatusTone)
    : 'neutral';
}

/** Nested `dayStatus` object from the attendance APIs. */
export function parseAttendanceDayStatus(value: unknown): AttendanceDayStatus | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const code = text(row.code);
  const label = text(row.label);
  if (!code || !label) return null;
  return {
    code,
    label,
    tone: tone(row.tone),
    notes: Array.isArray(row.notes)
      ? row.notes.map(text).filter((note): note is string => Boolean(note))
      : [],
    isFinal: row.isFinal === true,
    workingDay: row.workingDay !== false,
    presentDays: numberOr(row.presentDays, 0),
    absentDays: numberOr(row.absentDays, 0),
    leaveDays: numberOr(row.leaveDays, 0),
    workedMinutes: numberOrNull(row.workedMinutes),
    expectedWorkMinutes: numberOrNull(row.expectedWorkMinutes),
    halfDayWorkMinutes: numberOrNull(row.halfDayWorkMinutes),
  };
}

const TONE_BY_CODE: Record<string, AttendanceDayStatusTone> = {
  PRESENT: 'present',
  CHECKED_IN: 'present',
  LATE: 'late',
  WFH: 'wfh',
  ON_LEAVE: 'leave',
  HALF_DAY_LEAVE: 'leave',
  ABSENT: 'absent',
  LOP: 'absent',
  HALF_DAY: 'absent',
};

/**
 * Day status from a report row's values: the nested object (weekly days) or
 * the flat dayStatusCode / dayStatus / dayStatusNotes report columns.
 */
export function dayStatusFromReportValues(
  values: Record<string, unknown>,
): Pick<AttendanceDayStatus, 'code' | 'label' | 'tone' | 'notes'> | null {
  const nested = parseAttendanceDayStatus(values.dayStatus);
  if (nested) return nested;
  const code = text(values.dayStatusCode ?? values.day_status_code);
  const label = text(values.dayStatus ?? values.day_status);
  if (!code || !label) return null;
  const notes = text(values.dayStatusNotes ?? values.day_status_notes);
  return {
    code,
    label,
    tone: TONE_BY_CODE[code.toUpperCase()] ?? 'neutral',
    notes: notes ? notes.split(';').map(note => note.trim()).filter(Boolean) : [],
  };
}

/** Status label with late-arrival / leave-deduction notes on following lines. */
export function formatStatusWithNotes(
  label: string,
  notes: readonly string[] | null | undefined,
): string {
  return [label, ...(notes ?? [])].join('\n');
}
